/**
 * Auto-Resolution Workflow
 *
 * This workflow attempts to automatically resolve common support requests:
 * 1. Triggers when a new support request is created
 * 2. Analyzes if the issue is auto-resolvable
 * 3. Executes resolution if possible (password reset, access grant, etc.)
 * 4. Notifies user of resolution or escalates to human support
 */

import type { Workflow } from './types';

// =============================================================================
// WORKFLOW DEFINITION
// =============================================================================

export const autoResolutionWorkflow: Workflow = {
  id: 'auto-resolution',
  name: 'Risoluzione Automatica Supporto',
  description: 'Risolve automaticamente richieste comuni come reset password, concessione accessi, etc.',
  trigger: {
    type: 'event',
    config: {
      eventType: 'entity.created',
      entityType: 'support_request',
    },
  },
  steps: [
    {
      id: 'check_resolvable',
      name: 'Verifica Risolvibilità',
      type: 'ai_analyze',
      config: {
        prompt: `Analyze this support request and determine if it can be auto-resolved.

Request:
Subject: {subject}
Description: {description}
User: {userName}
User email: {userEmail}

Auto-resolvable categories:
1. PASSWORD_RESET: User forgot password
2. ACCESS_GRANT: User needs access to feature/resource
3. ACCOUNT_UNLOCK: Account locked due to failed logins
4. EMAIL_VERIFICATION: Need to resend verification email
5. SUBSCRIPTION_INFO: Question about subscription status
6. BASIC_HOWTO: Simple how-to question with documented answer

Respond in JSON:
{
  "isResolvable": true/false,
  "category": "PASSWORD_RESET|ACCESS_GRANT|ACCOUNT_UNLOCK|EMAIL_VERIFICATION|SUBSCRIPTION_INFO|BASIC_HOWTO|NOT_RESOLVABLE",
  "confidence": 0.0-1.0,
  "requiredData": {
    "field": "value needed for resolution"
  },
  "resolutionSteps": ["step 1", "step 2"]
}`,
        confidenceThreshold: 0.85,
      },
      next: ['resolve_if_possible'],
    },
    {
      id: 'resolve_if_possible',
      name: 'Esegui Risoluzione',
      type: 'custom',
      config: {
        // Execute resolution based on category
        resolutionHandlers: {
          PASSWORD_RESET: 'sendPasswordResetEmail',
          ACCESS_GRANT: 'grantUserAccess',
          ACCOUNT_UNLOCK: 'unlockUserAccount',
          EMAIL_VERIFICATION: 'resendVerificationEmail',
          SUBSCRIPTION_INFO: 'sendSubscriptionDetails',
          BASIC_HOWTO: 'sendDocumentationLink',
        },
        // Only execute if confidence is high
        requireHighConfidence: true,
        confidenceThreshold: 0.85,
        // Log all auto-resolutions for audit
        logResolution: true,
        // Update support request status
        markAsResolved: true,
      },
      condition: {
        field: 'isResolvable',
        operator: '==',
        value: true,
      },
      next: ['send_notification'],
      onError: 'continue',
    },
    {
      id: 'send_notification',
      name: 'Notifica Utente',
      type: 'send_notification',
      config: {
        type: 'in_app',
        // Different messages based on resolution status
        messageTemplates: {
          resolved: 'La tua richiesta è stata risolta automaticamente! {resolutionDetails}',
          escalated: 'La tua richiesta è stata inoltrata al team di supporto. Ti risponderemo presto.',
          failed: 'Non sono riuscito a risolvere automaticamente. Un operatore ti contatterà.',
        },
        sendEmail: true,
        includeResolutionSteps: true,
      },
    },
  ],
  enabled: true,
  version: 1,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * Validate if user data is sufficient for auto-resolution
 */
export function validateResolutionData(category: string, userData: Record<string, unknown>): boolean {
  const requiredFields: Record<string, string[]> = {
    PASSWORD_RESET: ['email'],
    ACCESS_GRANT: ['userId', 'resourceId'],
    ACCOUNT_UNLOCK: ['userId'],
    EMAIL_VERIFICATION: ['email'],
    SUBSCRIPTION_INFO: ['userId'],
    BASIC_HOWTO: ['topic'],
  };

  const required = requiredFields[category] || [];
  return required.every(field => userData[field] !== undefined && userData[field] !== null);
}

/**
 * Generate user-friendly resolution message
 */
export function formatResolutionMessage(category: string, data: Record<string, unknown>): string {
  const messages: Record<string, string> = {
    PASSWORD_RESET: `Ti abbiamo inviato un'email per reimpostare la password a ${data.email}`,
    ACCESS_GRANT: `L'accesso a "${data.resourceName}" è stato concesso`,
    ACCOUNT_UNLOCK: `Il tuo account è stato sbloccato. Puoi effettuare il login ora.`,
    EMAIL_VERIFICATION: `Ti abbiamo inviato una nuova email di verifica a ${data.email}`,
    SUBSCRIPTION_INFO: `Dettagli abbonamento inviati via email`,
    BASIC_HOWTO: `Trovi la risposta qui: ${data.documentationUrl}`,
  };

  return messages[category] || 'Richiesta elaborata con successo';
}
